// React hooks used for memoized lists, resize effects, refs, and local UI state.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";



// Hooks for the in progress queue section 
const useInProgressQueue = ({
  visibleImages,
  isInProgressQueueItem,
  isQueueItemQueued,
}) => {

  // Refs used by layout/resize observers.
  // Big progress row that can show cards
  const inProgressRowRef = useRef(null);


  // Separate ref for the compact bars container 
  const inProgressJobBarsRef = useRef(null);


  // UI state for in-progress section and responsive measurements.
  const [isInProgressCollapsed, setIsInProgressCollapsed] = useState(true);

  const [maxInProgressSlots, setMaxInProgressSlots] = useState(1);

  const [inProgressJobBarsWidth, setInProgressJobBarsWidth] = useState(0);



  // All in-progress items sorted oldest first.
  const inProgressQueueItems = useMemo(() => {

    // Filter to only items that are currently in progress, then sort by creation time.
    return visibleImages
      .filter((img) => isInProgressQueueItem(img))
      .slice()
      .sort((a, b) => {

        // Oldest first keeps queue order stable for cards and bar summaries.
        const aTime = new Date(a?.created || a?.started || 0).getTime();

        const bTime = new Date(b?.created || b?.started || 0).getTime();

        return aTime - bTime;

      });

  }, [visibleImages, isInProgressQueueItem]);



  // Watch row width and compute how many cards fit when expanded.
  const setupInProgressSlotsObserver = useCallback((rowElement) => {

    // If we don't have a row element to observe, we can't set up the observer.
    if (!rowElement) return undefined;

    // Keep this in sync with CSS card gap.
    const gapWidth = 12;

    const recalcVisibleSlots = () => {
    
      // Measure the full row width to see how many cards can fit.
      const rowWidth = rowElement.clientWidth || 0;

      // If we can't measure the row width for some reason, default to showing 1 card to avoid showing zero cards.
      if (rowWidth <= 0) return;

      // Measure one card to estimate how many cards can fit in the row.
      const sampleCard = rowElement.querySelector(".ai-in-progress-item");

      // If we can't find a sample card or measure its width, default to showing 1 card to avoid showing zero cards.
      const measuredCardWidth = sampleCard?.getBoundingClientRect().width || 250;

      // Calculate how many cards can fit in the available row width, accounting for gaps between cards. Always allow at least 1 card to be shown.
      const slotCount = Math.max(1, Math.floor((rowWidth + gapWidth) / (measuredCardWidth + gapWidth)));

      // Update state with the calculated number of visible slots for in-progress items.
      setMaxInProgressSlots(slotCount);
    
    };

    // Initial calculation to set up correct number of slots on first render.
    recalcVisibleSlots();

    // Set up a ResizeObserver to watch for changes in the row width and recalculate visible slots when it changes. Also listen for window resize events as a fallback.
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(recalcVisibleSlots)
      : null;

    // Start observing the row element for size changes to recalculate visible slots when the row width changes.
    resizeObserver?.observe(rowElement);

    // Listen for window resize events to recalculate visible slots when the window size changes, which could affect the row width.
    window.addEventListener("resize", recalcVisibleSlots);

    return () => {
    
      // Clean up the observer and event listener when the component unmounts 
      resizeObserver?.disconnect();

      // Remove the window resize event listener
      window.removeEventListener("resize", recalcVisibleSlots);
    
    };

  }, []);



  // Start slot observer only when section is expanded and has items.
  useEffect(() => {

    // If the in-progress section is collapsed or there are no in-progress items, 
    // we don't need to set up the observer since we won't be showing any cards. Return early to avoid unnecessary observer setup.
    if (isInProgressCollapsed || inProgressQueueItems.length === 0) return undefined;
    
    // Set up the observer on the row element to watch for width changes and calculate how many cards can fit when expanded.
    return setupInProgressSlotsObserver(inProgressRowRef.current);
  
  }, [isInProgressCollapsed, inProgressQueueItems.length, setupInProgressSlotsObserver]);



  // Items shown as cards in expanded mode.
  // If too many items, reserve one slot for the "+N more" card.
  const inProgressDisplayItems = useMemo(() => {
  
    // If there are no in-progress items, return an empty array since there's nothing to display.
    if (inProgressQueueItems.length === 0) return [];

    // If the number of in-progress items is less than or equal to the max slots available, show all items as cards without collapsing.
    if (inProgressQueueItems.length <= maxInProgressSlots) return inProgressQueueItems;

    // If there are more in-progress items than available slots, show as many items as can fit in the 
    // available slots minus one reserved for the "+N more" card, and keep the rest hidden behind the "+N more" indicator.
    const visibleCardSlots = Math.max(1, maxInProgressSlots - 1);

    return inProgressQueueItems.slice(0, visibleCardSlots);
  
  }, [inProgressQueueItems, maxInProgressSlots]);

  // Number hidden behind "+N more".
  const hiddenInProgressCount = Math.max(0, inProgressQueueItems.length - inProgressDisplayItems.length);

  // Items included in the collapsed aggregate progress area.
  // Excludes terminal statuses and items that already produced output.
  const aggregateInProgressItems = useMemo(() => {
  
    // Filter the visible images to include only those that are considered "in progress" 
    // based on the provided rules: they should not have a terminal status 
    // (like complete, error, or failed) and should not already have an image URL, output, or error message. 
    // This gives us a list of items that are actively in progress and can be summarized in the aggregate progress area when the section is collapsed.
    return visibleImages.filter((img) => {

      // if we dont hve an object to check return false
      if (!img) return false;

      // Exclude items with terminal statuses to keep the aggregate area focused on active progress.
      const status = String(img?.status || "").toLowerCase();

      // Define terminal statuses that indicate an item is no longer in progress. 
      // This is "complete", "error", and "failed" statuses
      const hasTerminalStatus = status === "complete" || status === "error" || status === "failed";

      // If the item has a terminal status, we consider it no longer in progress and exclude it from the aggregate progress area.
      if (hasTerminalStatus) return false;

      // Exclude items that already have an image URL, output, or error message.
      // This ensures that only actively in-progress items are included in the aggregate progress area.
      return !img?.image_url && !img?.output && !img?.error_message;
  
    });

  }, [visibleImages]);



  // compact progress bars.
  const inProgressJobSummaries = useMemo(() => {
  
    // Map the aggregate in-progress items to a summary format for the compact progress bars.
    return aggregateInProgressItems.map((img, index) => {

        const fallbackKey = `item-${index}`;

        // Use job id when available, fallback keeps key stable enough for display.
        const key = String(img?.job || fallbackKey);

        // While queued we show countdown, otherwise normal progress.
        const progress = Math.max(
          0,
          Math.min(100, Number(img?.countdown > 0 ? img?.countdown : img?.progress) || 0)
        );

        // Determine if the item is still in the queue phase (hasn't started processing yet) to show a different label/color in the UI.
        const createdMs = new Date(img?.created || img?.started || 0).getTime();

        return {

          key,

          // Phase drives bar color/label in UI.
          progress,
          phase: isQueueItemQueued(img) ? "queued" : "running",
          createdMs: Number.isFinite(createdMs) ? createdMs : 0,

        };

      })

      // Oldest first to match card order.
      .sort((a, b) => a.createdMs - b.createdMs);

  }, [aggregateInProgressItems, isQueueItemQueued]);



  // Track compact bars container width for responsive "visible vs hidden" calculation.
  useEffect(() => {
  
    // If we don't have a ref to the compact bars container, we can't set up the observer.
    const container = inProgressJobBarsRef.current;

    // If we don't have a container to observe, we can't measure the width or set up the observer, so we return early.
    if (!container) return undefined;

    // Function to measure the container width and update state, which will trigger a recalculation of how many bars can fit when expanded.
    const recalcWidth = () => setInProgressJobBarsWidth(container.clientWidth || 0);

    recalcWidth();

    // Set up a ResizeObserver to watch for changes in the container width and recalculate when it changes. Also listen for window resize events as a fallback.
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(recalcWidth)
      : null;

    // Start observing the container element for size changes to recalculate how many bars can fit when expanded.
    observer?.observe(container);

    window.addEventListener("resize", recalcWidth);

    return () => {

      // Destroy the observer and event listener when the component unmounts
      observer?.disconnect();

      window.removeEventListener("resize", recalcWidth);

    };

  }, [inProgressJobSummaries.length]);



  // Decide how many compact bars can fit.
  // If all cannot fit, keep room for a "+N more" badge.
  const inProgressJobBarsLayout = useMemo(() => {
    
    // If there are no in-progress jobs, return an empty layout with zero hidden count since there's nothing to display.
    if (inProgressJobSummaries.length === 0) {

      return { visibleJobs: [], hiddenCount: 0 };

    }

    // Before first measurement, show all bars.
    if (inProgressJobBarsWidth <= 0) {

      return { visibleJobs: inProgressJobSummaries, hiddenCount: 0 };

    }

    // width of each bar in the layout - keep in sync with CSS!
    const barWidth = 52;

    // gap between bars in the layout - keep in sync with CSS!
    const barGap = 10;

    // width of the "+N more" badge - keep in sync with CSS!
    const moreBadgeWidth = 84;

    // Max bars when there is no "+N more" badge.
    const maxWithoutMore = Math.max(1, Math.floor((inProgressJobBarsWidth + barGap) / (barWidth + barGap)));
   
    // If all bars can fit without needing a "+N more" badge, show them all.
    if (inProgressJobSummaries.length <= maxWithoutMore) {

      return { visibleJobs: inProgressJobSummaries, hiddenCount: 0 };

    }

    // Max bars when we also reserve width for "+N more".
    const maxWithMore = Math.max(
      1,
      Math.floor((inProgressJobBarsWidth - moreBadgeWidth + barGap) / (barWidth + barGap))
    );

    // Show as many bars as can fit, but at least one, and reserve rest in "+N more".
    const visibleCount = Math.min(maxWithMore, inProgressJobSummaries.length);

    // Return the bars that can be shown in the available width and the count of how many bars are hidden behind the "+N more" badge.
    return {

      visibleJobs: inProgressJobSummaries.slice(0, visibleCount),
      hiddenCount: Math.max(0, inProgressJobSummaries.length - visibleCount),

    };

  }, [inProgressJobBarsWidth, inProgressJobSummaries]);

  return {

    inProgressRowRef,
    inProgressJobBarsRef,
    isInProgressCollapsed,
    setIsInProgressCollapsed,
    inProgressQueueItems,
    inProgressDisplayItems,
    hiddenInProgressCount,
    inProgressJobBarsLayout,
    totalInProgressCount: aggregateInProgressItems.length,

  };

};

export default useInProgressQueue;
